/**
 * XR Controller/Hand Entity Management.
 *
 * Sets up Three.js controller and hand groups via renderer.xr,
 * and creates corresponding ECS entities with XRTracked tags.
 */

import type { App } from '../core/App.js';
import type { EngineRenderer } from '../render/Renderer.js';
import { Transform } from '../ecs/Component.js';
import { XRTracked, XRController } from './XRComponents.js';
import type { XRSessionState } from './types.js';
import { Logger } from '../core/Logger.js';

// Listener handles stashed per-setup on an XRSessionState so they can be
// removed in teardown. Previously this was a module-level singleton — two
// XRModule instances (e.g. unit tests, or an App + embedded debug viewer)
// shared the same map and clobbered each other's listener registrations on
// session start/end, producing leaks and stale listeners firing after
// teardown.
interface ControllerListenerMap {
  connected: (event: any) => void;
  disconnected: () => void;
}

type StateWithListeners = XRSessionState & {
  _controllerListeners?: Map<number, ControllerListenerMap>;
};

function _getListenerMap(state: XRSessionState): Map<number, ControllerListenerMap> {
  const s = state as StateWithListeners;
  if (!s._controllerListeners) {
    s._controllerListeners = new Map();
  }
  return s._controllerListeners;
}

/**
 * Set up Three.js controller/grip/hand groups and create ECS entities.
 * Called when an XR session starts.
 */
export function setupControllerEntities(
  app: App,
  state: XRSessionState,
  renderer: EngineRenderer,
): void {
  const xr = renderer.native.xr;
  const scene = app.world.scene;

  // Set up two controller slots (index 0 and 1)
  for (let i = 0; i < 2; i++) {
    // Target ray group (for pointing)
    const controller = xr.getController(i);
    scene.add(controller);
    state.controllerGroups.set(i, controller);

    // Grip group (for held objects / controller model attachment)
    const grip = xr.getControllerGrip(i);
    scene.add(grip);
    state.controllerGripGroups.set(i, grip);

    // Hand tracking group
    const hand = xr.getHand(i);
    scene.add(hand);
    state.handGroups.set(i, hand);

    // Create ECS entity for this controller slot
    const handedness = i === 0 ? 'left' : 'right';
    const entityId = app.world.createEntity(`XR_${handedness}`)
      .add(Transform)
      .add(XRController, {
        handedness,
        targetRayMode: 'tracked-pointer',
        hasHand: false,
      })
      .tag(XRTracked)
      .build();

    state.controllerEntities.set(i, entityId);

    // Listen for connected/disconnected on the controller group.
    // Store handlers so teardown can remove them (controller groups are cached
    // by Three.js WebXRManager and persist across sessions).
    const onConnected = (event: any) => {
      const inputSource: XRInputSource = event.data;
      const comp = app.world.getComponent(entityId, XRController);
      if (comp) {
        comp.handedness = inputSource.handedness;
        comp.targetRayMode = inputSource.targetRayMode;
        comp.hasHand = !!inputSource.hand;
      }
      Logger.debug('XR', `Controller ${i} connected: ${inputSource.handedness} (${inputSource.targetRayMode})`);
    };
    const onDisconnected = () => {
      Logger.debug('XR', `Controller ${i} disconnected`);
    };

    controller.addEventListener('connected', onConnected);
    controller.addEventListener('disconnected', onDisconnected);
    _getListenerMap(state).set(i, { connected: onConnected, disconnected: onDisconnected });

    // Replay a connect that already happened: three's inputsourceschange
    // listener is live from the top of setSession, so controllers in hand at
    // session start are connected during its reference-space await — BEFORE
    // 'sessionstart' runs this setup. The missed 'connected' left the slot's
    // XRController component at its slot-default handedness ('left' for slot 0
    // even when the right controller sits there). Read three's live slot table.
    const already = (xr as unknown as { _controllerInputSources?: (XRInputSource | null)[] })
      ._controllerInputSources?.[i];
    if (already) onConnected({ data: already });
  }
}

/**
 * Tear down controller entities and remove Three.js groups from scene.
 * Called when an XR session ends.
 */
export function teardownControllerEntities(
  app: App,
  state: XRSessionState,
): void {
  const scene = app.world.scene;

  // Remove event listeners from controller groups before removing from scene.
  // Three.js caches these groups, so stale listeners would accumulate.
  const listenerMap = _getListenerMap(state);
  for (const [idx, group] of state.controllerGroups) {
    const handlers = listenerMap.get(idx);
    if (handlers) {
      // Cast: controllerGroups stores Group but these are XRTargetRaySpace
      // instances returned by getController() which have XR-specific events.
      (group as any).removeEventListener('connected', handlers.connected);
      (group as any).removeEventListener('disconnected', handlers.disconnected);
    }
    scene.remove(group);
  }
  listenerMap.clear();

  for (const [, group] of state.controllerGripGroups) {
    scene.remove(group);
  }
  for (const [, group] of state.handGroups) {
    scene.remove(group);
  }

  // Destroy ECS entities
  for (const [, entityId] of state.controllerEntities) {
    if (app.world.isAlive(entityId)) {
      app.world.destroyEntity(entityId);
    }
  }

  state.controllerGroups.clear();
  state.controllerGripGroups.clear();
  state.handGroups.clear();
  state.controllerEntities.clear();
}
